import type {ExtractPropsWithoutRef} from '@/types';

import {IconChevronTop, IconChevronBottom} from '@travel-frontend/icons';
import {Button, useBooleanToggle} from '@travel-frontend/ui-components';
import {useTranslation} from 'react-i18next';

import {Container} from '@/components/Collapsible/components/Container';
import {ContainerWithFade} from '@/components/Collapsible/components/ContainerWithFade';
import {Content} from '@/components/Collapsible/components/Content';
import {Headline} from '@/components/Collapsible/components/Headline';
import {Trigger} from '@/components/Collapsible/components/Trigger';

type Props = {
  headline: React.ReactNode;
  /**
   * Needs to be a list of `React.ReactNode` in order to determine how many items are inside to content and if the Group should be expanable in regards to {@link Props.maxVisibleItems}
   */
  children: Array<React.ReactNode>;
  /**
   * Wheter the complete box is collapsible with a button next to the headline (aside {@link Props.maxVisibleItems})
   */
  isCollapsible?: boolean;
  /**
   * When {@link Props.isCollapsible} is set to `true` this will determine wheter the box is collapsed by default or not
   */
  isCollapsed?: boolean;
  /**
   * Determines how many items are visible by default until toogle feature is shown.
   * @default 5
   */
  maxVisibleItems?: number;
  /**
   * Callback that is called when the collapse state changes.
   */
  onCollapseChange?: (isCollapsed: boolean) => void;
  /**
   * Optional text to show next to the chevron icon in the headline
   */
  triggerText?: string;
  hasFadeOut?: boolean;
} & ExtractPropsWithoutRef<'div'>;

export const Collapsible = ({
  children,
  headline,
  isCollapsible,
  isCollapsed,
  maxVisibleItems = 5,
  hasFadeOut,
  onCollapseChange,
  triggerText,
  ...restProps
}: Props) => {
  const {t} = useTranslation('general');
  const [outerOpen, setOuterOpen] = useBooleanToggle(!isCollapsed);
  const [innerOpen, setInnerOpen] = useBooleanToggle(false);

  const count = children.length;
  const visibleItems = children.slice(0, maxVisibleItems);
  const hiddenItems = children.slice(maxVisibleItems);
  const areControlsNecessary = hiddenItems.length > 0;

  return (
    <Container {...restProps} open={isCollapsible ? outerOpen : true}>
      <Headline
        triggerText={triggerText}
        isCollapsible={isCollapsible}
        isOpen={outerOpen}
        onToggleClick={() => {
          setOuterOpen();
          onCollapseChange?.(outerOpen);
        }}
      >
        {headline}
      </Headline>
      <Content>
        <ContainerWithFade
          open={areControlsNecessary ? innerOpen : true}
          hasFadeOut={hasFadeOut}
        >
          {visibleItems}
          <Content>{hiddenItems}</Content>
          {areControlsNecessary && (
            <Trigger asChild>
              <Button
                category="tertiary"
                variant="interaction"
                icon={innerOpen ? <IconChevronTop /> : <IconChevronBottom />}
                iconPosition="right"
                onClick={setInnerOpen}
              >
                {innerOpen ? t('showLess') : t('showAll', {count})}
              </Button>
            </Trigger>
          )}
        </ContainerWithFade>
      </Content>
    </Container>
  );
};
